import type { Project } from '../types';
import { getPersonAllocations } from './allocation';
import { getProjectMilestones } from './milestoneProgress';
import { parseYMD } from './echeances';

export interface PersonAllocationBlock {
  projectId: number;
  projectName: string;
  role: string;         // free-text précision
  projectRole: string;  // [Proposition: roles-projet] one of the four roles ('' if unset)
  hoursPerWeek: number;
  start: string; // 'YYYY-MM-DD'
  end: string;   // 'YYYY-MM-DD'
}

// Best-available timeframe for a project's active work: earliest milestone
// start (or its own end, if no start is set) through the project's own
// deadline — falling back to the latest milestone end when the project has
// none. Scoping allocations carry a %/role but no dates of their own, so this
// is the closest thing to "when is this person busy on this project".
function getProjectSpan(project: Project, projectDeadlines: Map<number, string>): { start: string; end: string } | null {
  const milestones = getProjectMilestones(project);
  const starts = milestones.map(m => m.estimated_start || m.estimated_end).filter(Boolean);
  const ends = milestones.map(m => m.estimated_end).filter(Boolean);
  const deadline = projectDeadlines.get(project.id) || null;
  const start = starts.length > 0 ? starts.reduce((a, b) => (parseYMD(a) <= parseYMD(b) ? a : b)) : null;
  const end = deadline || (ends.length > 0 ? ends.reduce((a, b) => (parseYMD(a) >= parseYMD(b) ? a : b)) : null);
  if (!start || !end) return null;
  return { start, end };
}

// One block per project a person is allocated hours on, detached from any
// single project — so several collaborators' loads can be compared side by
// side over time. `activeProjects` should already be filtered to the
// Planning/Execution portfolio; projects with no derivable timeframe (no
// milestone dates and no deadline) are skipped since they can't be placed on
// a timeline.
export function getPersonAllocationBlocks(
  personName: string,
  activeProjects: Project[],
  projectDeadlines: Map<number, string>
): PersonAllocationBlock[] {
  const projectsById = new Map(activeProjects.map(p => [p.id, p]));
  const blocks: PersonAllocationBlock[] = [];
  for (const a of getPersonAllocations(personName, activeProjects)) {
    if (a.hoursPerWeek <= 0) continue;
    const project = projectsById.get(a.projectId);
    const span = project ? getProjectSpan(project, projectDeadlines) : null;
    if (!span) continue;
    blocks.push({ projectId: a.projectId, projectName: a.projectName, role: a.role, projectRole: a.projectRole, hoursPerWeek: a.hoursPerWeek, start: span.start, end: span.end });
  }
  return blocks;
}
